/**
 * The other end of the wire: a client for a hosted world.
 *
 * Written against `WebSocket` and `fetch` and nothing else, both of which are
 * globals in a browser and in node, so this one file is the client for the
 * game, for `test/host-server.ts`, and for whatever probe wants to poke a
 * running world. There is no second implementation, which is the same rule
 * `src/sim/` keeps one level down.
 *
 * ## What it does and does not decide
 *
 * It owns the wire and the clock, and nothing else. It will:
 *
 * - authenticate, and recover a session id so a reconnect is the same god;
 * - fetch the world over HTTP and hand back a decoded `SimulationSnapshot`;
 * - keep the canonical clock, **extrapolated between ticks** rather than
 *   stepped, so a renderer reading it forty times a second gets a smooth
 *   number instead of the last heartbeat's;
 * - send intents and match acks back to them;
 * - hand every server message to a callback.
 *
 * It will not adopt a world, draw anything, hold a `Simulation`, or decide
 * what to do when the server says resync. Those are the host application's,
 * and keeping them out is what lets the same file serve a renderer and a test.
 *
 * ## The clock is the subtle part
 *
 * A `tick` arrives once a second or so. A frame happens sixty times a second.
 * If a client read world time only at ticks it would render the world in
 * one-second steps — at 192× that is three world-minutes of sun per jump.
 *
 * So a tick is stored as an ANCHOR, exactly the record `src/host/time.ts`
 * already defines, and `worldTimeAt(nowMs)` extrapolates from it at the
 * server's own rate. The client is therefore never *ahead* of the server by
 * more than its own clock drift, and it is never behind by a whole heartbeat.
 * A paused world extrapolates to a standstill for free, because `paused` is a
 * field on the anchor and `worldTimeAt` already honours it.
 *
 * That is also the honest statement of what a client's clock IS: an estimate
 * of the server's, re-anchored on every heartbeat, and never a source of
 * truth. Law 27 says the shared world has one clock, and it is not this one.
 */

import {
  decodeSnapshot, type SimulationSnapshot,
} from '@/sim/persistence/snapshot';
import { newClockState, worldTimeAt, type WorldClockState } from './time';
import { PROTOCOL_VERSION } from './intent';
import type {
  AckMessage, ClosingMessage, ErrorMessage, EventsMessage, ServerMessage,
  SnapshotReadyMessage, TickMessage, WelcomeMessage,
} from './protocol';

export interface ConnectOptions {
  /** `http://127.0.0.1:8787` — the server's HTTP origin. `ws://` is derived. */
  origin: string;
  /** The friend token. Sent as a query parameter, so keep the origin private. */
  token: string;
  /** A session id held from a previous connection, to come back as the same god. */
  session?: string;
  /**
   * What to call this player to the others. Cleaned and bounded by the server
   * (`cleanName`), and session chrome only — it never reaches the world.
   */
  name?: string;
  /** How long to wait for `welcome` before giving up, milliseconds. */
  timeoutMs?: number;
  onTick?: (m: TickMessage) => void;
  onEvents?: (m: EventsMessage) => void;
  onAck?: (m: AckMessage) => void;
  /** The world was replaced or the stream fell behind: refetch it. */
  onSnapshotReady?: (m: SnapshotReadyMessage) => void;
  onError?: (m: ErrorMessage) => void;
  onClosing?: (m: ClosingMessage) => void;
  /** The socket went away — network, server exit, or `close()`. */
  onClosed?: (info: { code: number; reason: string; deliberate: boolean }) => void;
  /** Anything the client could not make sense of. Never throws at the socket. */
  onProtocolError?: (why: string, raw: string) => void;
}

export interface WorldConnection {
  readonly welcome: WelcomeMessage;
  readonly deityId: string;
  readonly session: string;
  readonly worldId: string;
  /** What the server took this player's name to be — cleaned, possibly absent. */
  readonly name: string | undefined;
  readonly seed: number;
  /** Metres; the same bound the server validates coordinates against. */
  readonly halfExtent: number;

  /**
   * Canonical world time, extrapolated from the last heartbeat. Safe to call
   * every frame; it is arithmetic on a stored anchor, not a network read.
   */
  worldTimeAt(nowMs: number): number;
  /** The stored anchor itself, for a caller that wants to reason about drift. */
  readonly clock: WorldClockState;
  /** World-seconds the server said it still owed at the last heartbeat. */
  readonly lag: number;
  /** Where every connected god is looking, as of the last heartbeat. */
  readonly presences: TickMessage['presences'];

  /**
   * Fetch the world over HTTP and decode it through the real decoder — so a
   * version mismatch is a throw here rather than a strange world later.
   */
  fetchWorld(): Promise<SimulationSnapshot>;

  /**
   * Send an intent. Returns the client sequence it was given, which is what
   * the matching `ack` will echo. The payload is NOT validated here: the
   * server validates, and a client that pre-validated would be a second
   * implementation of the rules, drifting.
   */
  send(kind: string, payload?: unknown): number;

  /** Send, and resolve when the matching ack arrives. */
  request(kind: string, payload?: unknown, timeoutMs?: number): Promise<AckMessage>;

  readonly connected: boolean;
  close(): void;
}

const wsOrigin = (origin: string): string =>
  origin.replace(/^http/, 'ws').replace(/\/+$/, '');

/**
 * Open a connection and resolve once the server has said who you are.
 *
 * Rejects rather than resolving into a half-usable object: a caller that got a
 * `WorldConnection` back can rely on `deityId`, `worldId` and the clock being
 * real. Protocol-version mismatch rejects too, loudly, because a client
 * guessing at a newer protocol is the same failure `decodeSnapshot` refuses.
 */
export function connectToWorld(opts: ConnectOptions): Promise<WorldConnection> {
  const base = opts.origin.replace(/\/+$/, '');
  const url = `${wsOrigin(base)}/world/stream?token=${encodeURIComponent(opts.token)}` +
    (opts.session ? `&session=${encodeURIComponent(opts.session)}` : '') +
    (opts.name ? `&name=${encodeURIComponent(opts.name)}` : '');

  return new Promise<WorldConnection>((resolve, reject) => {
    let ws: WebSocket;
    try { ws = new WebSocket(url); } catch (e) { reject(e as Error); return; }

    let welcome: WelcomeMessage | null = null;
    let clock: WorldClockState = newClockState(0, 0);
    let lag = 0;
    let presences: TickMessage['presences'] = [];
    let seq = 0;
    let deliberate = false;
    const pending = new Map<number, {
      go: (m: AckMessage) => void; no: (e: Error) => void; timer: ReturnType<typeof setTimeout>;
    }>();

    const settleTimer = setTimeout(() => {
      reject(new Error(`no welcome from ${base} within ${opts.timeoutMs ?? 8000} ms`));
      try { ws.close(); } catch { /* already gone */ }
    }, opts.timeoutMs ?? 8000);

    const fail = (why: string, raw: string) => opts.onProtocolError?.(why, raw);

    ws.addEventListener('message', (ev: MessageEvent) => {
      const raw = typeof ev.data === 'string' ? ev.data : String(ev.data);
      let m: ServerMessage;
      try { m = JSON.parse(raw) as ServerMessage; } catch { fail('not JSON', raw); return; }
      if (!m || typeof m !== 'object' || typeof m.type !== 'string') {
        fail('not a server message', raw);
        return;
      }
      switch (m.type) {
        case 'welcome': {
          clearTimeout(settleTimer);
          if (m.protocolVersion !== PROTOCOL_VERSION) {
            reject(new Error(
              `server speaks protocol ${m.protocolVersion}, this build speaks ` +
              `${PROTOCOL_VERSION} — refusing to guess`));
            try { ws.close(); } catch { /* already gone */ }
            return;
          }
          welcome = m;
          // The first anchor. Every later `tick` replaces it; nothing here ever
          // advances a counter of its own.
          clock = {
            anchorRealMs: Date.now(), anchorWorldTime: m.worldTime,
            timeScale: m.timeScale, paused: m.paused,
          };
          resolve(connection);
          break;
        }
        case 'tick':
          clock = {
            anchorRealMs: Date.now(), anchorWorldTime: m.worldTime,
            timeScale: m.timeScale, paused: m.paused,
          };
          lag = m.lag;
          presences = m.presences;
          opts.onTick?.(m);
          break;
        case 'events':
          opts.onEvents?.(m);
          break;
        case 'ack': {
          const waiting = pending.get(m.clientSeq);
          if (waiting) {
            clearTimeout(waiting.timer);
            pending.delete(m.clientSeq);
            waiting.go(m);
          }
          opts.onAck?.(m);
          break;
        }
        case 'snapshot-ready':
          opts.onSnapshotReady?.(m);
          break;
        case 'error':
          opts.onError?.(m);
          break;
        case 'closing':
          opts.onClosing?.(m);
          break;
        default:
          fail(`unknown message type`, raw);
      }
    });

    ws.addEventListener('error', () => {
      if (!welcome) {
        clearTimeout(settleTimer);
        reject(new Error(`could not connect to ${base}`));
      }
    });

    ws.addEventListener('close', (ev: CloseEvent) => {
      clearTimeout(settleTimer);
      for (const [, w] of pending) {
        clearTimeout(w.timer);
        w.no(new Error('connection closed before the ack arrived'));
      }
      pending.clear();
      if (!welcome) reject(new Error(`connection to ${base} closed before welcome`));
      opts.onClosed?.({ code: ev.code, reason: ev.reason, deliberate });
    });

    const connection: WorldConnection = {
      get welcome() { return welcome!; },
      get deityId() { return welcome!.deityId; },
      get session() { return welcome!.session; },
      get name() { return welcome!.name; },
      get worldId() { return welcome!.worldId; },
      get seed() { return welcome!.seed; },
      get halfExtent() { return welcome!.halfExtent; },
      get clock() { return clock; },
      get lag() { return lag; },
      get presences() { return presences; },
      get connected() { return ws.readyState === ws.OPEN; },

      worldTimeAt: (nowMs: number) => worldTimeAt(clock, nowMs),

      async fetchWorld(): Promise<SimulationSnapshot> {
        const path = welcome?.snapshotPath ?? '/world/snapshot';
        const r = await fetch(`${base}${path}?token=${encodeURIComponent(opts.token)}`);
        if (!r.ok) throw new Error(`${path} answered ${r.status}`);
        const snap = decodeSnapshot(await r.text());
        if (welcome && snap.seed !== welcome.seed) {
          throw new Error(
            `the world served is seed ${snap.seed}, but this connection is to ` +
            `world ${welcome.seed} — refusing to adopt history onto the wrong baseline`);
        }
        return snap;
      },

      send(kind: string, payload?: unknown): number {
        const s = ++seq;
        ws.send(JSON.stringify({ seq: s, kind, ...(payload === undefined ? {} : { payload }) }));
        return s;
      },

      request(kind: string, payload?: unknown, timeoutMs = 5000): Promise<AckMessage> {
        return new Promise<AckMessage>((go, no) => {
          const s = connection.send(kind, payload);
          const timer = setTimeout(() => {
            pending.delete(s);
            no(new Error(`no ack for ${kind} #${s} within ${timeoutMs} ms`));
          }, timeoutMs);
          pending.set(s, { go, no, timer });
        });
      },

      close(): void {
        deliberate = true;
        try { ws.close(1000, 'client closing'); } catch { /* already gone */ }
      },
    };
  });
}
